import { ApiList } from "../types/common";
import { ApiJob, ApiJobContent, ApiJobNote, JobWarning, ListedJob, NewJobNote } from "../types/jobs";
import { AbstractBaseApi, ApiMethod, Params, SupportedContentType } from "./AbstractBaseApi";

interface ListJobOptions {
  blockingTask?: string;
  modifiedAfter?: number;
  modifiedBefore?: number;
  orderBy?: string;
  status?: string;
  type?: string;
  name_starts_with?: string;
}

type JobCheckinStatus = "aborted" | "completed";

export class JobsApi extends AbstractBaseApi {
  private static JOBS_ENDPOINT = "/jobs";
  public async getJob(jobId: string): Promise<ApiJob> {
    return this.makeApiRequest<SupportedContentType.Json, ApiJob>(
      ApiMethod.Get,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      undefined,
      SupportedContentType.Json,
    );
  }
  public async checkinJob(jobId: string, status: JobCheckinStatus): Promise<{ success: true }> {
    return this.makeApiRequest<SupportedContentType.Json, { success: true }>(
      ApiMethod.Post,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/checkin`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      JSON.stringify({ status }),
      SupportedContentType.Json,
    );
  }
  public async checkoutJob(jobId: string): Promise<ApiJob> {
    return this.makeApiRequest<SupportedContentType.Json, ApiJob>(
      ApiMethod.Post,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/checkout`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      undefined,
      SupportedContentType.Json,
    );
  }

  public async pushWarning(jobId: string, warning: JobWarning): Promise<ApiJob> {
    if (!warning.value) {
      delete warning.value;
    }
    return this.makeApiRequest<SupportedContentType.Json, ApiJob>(
      ApiMethod.Post,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/warnings`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      JSON.stringify([warning]),
      SupportedContentType.Json,
    );
  }

  public async addNote(jobId: string, content: string): Promise<ApiJobNote> {
    const body: NewJobNote = { content };
    return this.makeApiRequest<SupportedContentType.Json, ApiJobNote>(
      ApiMethod.Post,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/notes`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      JSON.stringify(body),
      SupportedContentType.Json,
    );
  }

  public async fetchNotes(jobId: string): Promise<Array<ApiJobNote>> {
    let totalCount = -1;
    let notes: Array<ApiJobNote> = [];
    do {
      const result = await this.makeApiRequest<SupportedContentType.Json, ApiList<ApiJobNote>>(
        ApiMethod.Get,
        `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/notes`,
        true,
        SupportedContentType.Json,
        { limit: "25", offset: notes.length.toString(10) },
        undefined,
        undefined,
        SupportedContentType.Json,
      );
      if (totalCount < 0) {
        totalCount = result.total_count;
        notes = result.list;
        continue;
      }
      if (result.total_count !== totalCount) {
        // the total count changed while we were fetching so start over
        totalCount = -1;
        notes = [];
        continue;
      }
      notes = notes.concat(result.list);
    } while (totalCount < 0 || notes.length < totalCount);
    return notes;
  }

  public async deleteNote(jobId: string, noteId: string): Promise<string> {
    const result = await this.makeApiRequest<SupportedContentType.Json, { id: string }>(
      ApiMethod.Delete,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/notes/${encodeURIComponent(noteId)}`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      undefined,
      SupportedContentType.Json,
    );
    return result.id;
  }

  public async updateNote(jobId: string, noteId: string, content: string): Promise<ApiJobNote> {
    return this.makeApiRequest<SupportedContentType.Json, ApiJobNote>(
      ApiMethod.Put,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/notes/${encodeURIComponent(noteId)}`,
      true,
      SupportedContentType.Json,
      undefined,
      undefined,
      JSON.stringify({ content }),
      SupportedContentType.Json,
    );
  }

  public async listJobContentForRole(
    jobId: string,
    role: string,
    limit: number,
    offset: number,
    signUris?: boolean,
  ): Promise<ApiList<ApiJobContent>> {
    const query: Params = {
      limit: limit.toString(10),
      offset: offset.toString(10),
    };
    if (signUris === true) {
      query.sign_uris = "true";
    }
    return this.makeApiRequest<SupportedContentType.Json, ApiList<ApiJobContent>>(
      ApiMethod.Get,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/contents/${encodeURIComponent(role)}`,
      true,
      SupportedContentType.Json,
      query,
      undefined,
      undefined,
      SupportedContentType.Json,
    );
  }
  public async listJobs(
    limit: number,
    offset: number,
    options?: ListJobOptions,
  ): Promise<ApiList<ListedJob>> {
    const query: Params = {
      limit: limit.toString(10),
      offset: offset.toString(10),
    };
    if (options !== undefined) {
      if (options.blockingTask) {
        query.blocking_task = options.blockingTask;
      }
      if (options.modifiedAfter !== undefined) {
        query.modified_after = options.modifiedAfter.toString(10);
      }
      if (options.modifiedBefore !== undefined) {
        query.modified_before = options.modifiedBefore.toString(10);
      }
      if (options.orderBy) {
        query.order_by = options.orderBy;
      }
      if (options.status) {
        query.status = options.status;
      }
      if (options.type) {
        query.type = options.type;
      }
      if (options.name_starts_with) {
        query.name_starts_with = options.name_starts_with;
      }
    }
    return this.makeApiRequest<SupportedContentType.Json, ApiList<ListedJob>>(
      ApiMethod.Get,
      JobsApi.JOBS_ENDPOINT,
      true,
      SupportedContentType.Json,
      query,
      undefined,
      undefined,
      SupportedContentType.Json,
    );
  }

  public async saveContent(
    jobId: string,
    role: string,
    order: number,
    content: string,
    isNew: boolean,
    type?: string,
    name?: string,
  ): Promise<ApiJobContent> {
    const customHeaders: Params = {
      "Content-type": "application/octet-stream",
    };
    if (type) {
      customHeaders["x-type"] = type;
    }
    if (name) {
      customHeaders["x-name"] = name;
    }
    return this.makeApiRequest<SupportedContentType.Json, ApiJobContent>(
      isNew ? ApiMethod.Post : ApiMethod.Put,
      `${JobsApi.JOBS_ENDPOINT}/${encodeURIComponent(jobId)}/contents/${encodeURIComponent(
        role,
      )}/${encodeURIComponent(order.toString(10))}`,
      true,
      SupportedContentType.Json,
      undefined,
      customHeaders,
      content,
      SupportedContentType.Binary,
    );
  }
}
